import type { AssetDetail } from "@/hooks/useAssetDetail";
import { normalizeAssetTags } from "@/lib/normalize-asset-tags";

/**
 * What `get_asset_detail` answers:
 *   - `null`              — no listing has (or had) this slug;
 *   - `{ slug, hidden }`  — the listing exists but this caller may not see it
 *                           (draft, in review, private, archived and not held…);
 *   - the full detail     — otherwise.
 */
export type AssetDetailLookup =
  | { kind: "missing" }
  | { kind: "hidden"; slug: string }
  | { kind: "visible"; asset: AssetDetail };

export function readAssetDetailRpc(data: unknown): AssetDetailLookup {
  if (data == null || typeof data !== "object") return { kind: "missing" };
  const row = data as { hidden?: unknown; slug?: unknown };
  if (row.hidden === true) {
    return { kind: "hidden", slug: typeof row.slug === "string" ? row.slug : "" };
  }
  return { kind: "visible", asset: parseAssetDetailFromRpc(data) };
}

/** Normalize RPC JSON (tags shape, review defaults) for SSR + client. */
export function parseAssetDetailFromRpc(data: unknown): AssetDetail {
  const asset = data as AssetDetail & { tags?: unknown };
  return {
    ...asset,
    images: Array.isArray(asset.images) ? asset.images : [],
    tags: normalizeAssetTags(asset.tags),
    avg_rating: asset.avg_rating ?? null,
    review_count: asset.review_count ?? 0,
  };
}
